import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import { Phone, Mail, Clock, DollarSign, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

export default function PipelinePage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const { settings, formatCurrency, formatDate } = useSettings();
  const navigate = useNavigate();
  const STAGES = settings.pipeline_stages || [];

  const fetchLeads = useCallback(async () => {
    try {
      const res = await leadsAPI.list({ limit: 200 });
      setLeads(res.data.leads);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDragStart = (e, leadId) => {
    setDraggedId(leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedId) return;

    const lead = leads.find(l => l.id === draggedId);
    if (!lead || lead.stage === newStage) { setDraggedId(null); return; }

    // Optimistic update
    setLeads(prev => prev.map(l => l.id === draggedId ? { ...l, stage: newStage } : l));
    setDraggedId(null);

    try {
      await leadsAPI.updateStage(draggedId, newStage);
      toast.success(`Moved to ${newStage}`);
    } catch {
      fetchLeads();
      toast.error('Failed to move lead');
    }
  };

  const groupedLeads = STAGES.reduce((acc, stage) => {
    acc[stage.key] = leads.filter(l => l.stage === stage.key);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-4 fade-in">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="flex gap-4 overflow-x-auto">
          {[...Array(5)].map((_, i) => <div key={i} className="min-w-[280px] h-96 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-5 fade-in" data-testid="pipeline-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Pipeline</h1>
        <p className="text-muted-foreground text-xs sm:text-sm">{leads.length} leads across {STAGES.length} stages</p>
      </div>

      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" style={{ minHeight: 'calc(100vh - 220px)' }}>
        {STAGES.map((stage) => {
          const stageLeads = groupedLeads[stage.key] || [];
          const isDropTarget = dragOverStage === stage.key;

          return (
            <div
              key={stage.key}
              className={`min-w-[240px] sm:min-w-[280px] w-[240px] sm:w-[280px] flex-shrink-0 rounded-lg border transition-colors ${
                isDropTarget ? 'kanban-drop-target' : 'border-border/40 bg-muted/20'
              }`}
              onDragOver={(e) => handleDragOver(e, stage.key)}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => handleDrop(e, stage.key)}
              data-testid={`pipeline-stage-${stage.key}`}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
                  <span className="text-sm font-semibold">{stage.label}</span>
                </div>
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-medium text-muted-foreground">
                  {stageLeads.length}
                </span>
              </div>

              {/* Cards */}
              <div className="p-2 space-y-2 min-h-[100px] max-h-[calc(100vh-280px)] overflow-y-auto">
                {stageLeads.map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead.id)}
                    onDragEnd={() => setDraggedId(null)}
                    onClick={() => navigate(`/leads/${lead.id}`, { 
                      state: { 
                        from: '/pipeline',
                        leadIds: leads.map(l => l.id)
                      } 
                    })}
                    className={`bg-background border border-border/60 rounded-md p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-all group ${
                      draggedId === lead.id ? 'opacity-50 rotate-1' : ''
                    }`}
                    data-testid={`pipeline-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                          {lead.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium leading-tight">{lead.name}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{lead.source?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <div className="mt-2.5 space-y-1">
                      {lead.budget_max && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <DollarSign className="w-3 h-3" />
                          <span className="mono">{formatCurrency(lead.budget_max)}</span>
                        </div>
                      )}
                      {lead.next_follow_up && (
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{formatDate(lead.next_follow_up)}</span>
                        </div>
                      )}
                    </div>

                    {lead.assigned_to_name && (
                      <div className="mt-2.5 pt-2 border-t border-border/30 flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">{lead.assigned_to_name}</span>
                        <Badge variant="secondary" className="text-[8px] h-4 px-1.5 capitalize">{lead.property_interest}</Badge>
                      </div>
                    )}
                  </div>
                ))}
                {stageLeads.length === 0 && (
                  <div className="text-center py-8 text-xs text-muted-foreground">
                    Drop leads here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
